import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import NotFound from './Notfound';
import { MediaTile, MediaLightbox, isPlayable } from '../components/MediaGallery';
import { RateCardView } from '../components/RateCard';
import { platformLabel } from '../components/CampaignForm';
import { ReputationPanel } from '../components/ReputationBadge';
import { AvailabilityChip, hasAvailability } from '../components/AvailabilityPanel';
import { mediaKitUrl } from '../utils/siteUrl';

const apiEndpoint = process.env.REACT_APP_API_URL;

const compact = (value) => {
    const n = Number(value || 0);
    if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
    return `${n}`;
};

const formatDate = (value) => (value
    ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '');

function Stat({ value, label, tone = 'slate' }) {
    const tones = {
        slate: 'text-slate-900',
        primary: 'text-primary-700',
        emerald: 'text-emerald-700',
    };
    return (
        <div className="rounded-2xl bg-white border border-slate-200 p-3.5 text-center">
            <p className={`text-xl sm:text-2xl font-black tabular-nums ${tones[tone]}`}>{value}</p>
            <p className="text-[10px] sm:text-[11px] text-slate-500 font-semibold mt-0.5 leading-tight">{label}</p>
        </div>
    );
}

function Section({ title, badge, children }) {
    return (
        <section className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base font-black text-slate-900">{title}</h2>
                {badge}
            </div>
            {children}
        </section>
    );
}

/**
 * A percent split as a labelled bar list. Bars are scaled against the largest
 * row rather than against 100, so a split that tops out at 34% still reads as
 * a shape instead of five near-empty tracks.
 */
function SplitBars({ rows }) {
    if (!rows?.length) return null;
    const max = Math.max(...rows.map(row => Number(row.percent) || 0), 1);

    return (
        <div className="space-y-2">
            {rows.map((row, index) => (
                <div key={index}>
                    <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-700 truncate">{row.label}</span>
                        <span className="text-slate-500 tabular-nums font-bold flex-shrink-0 ml-2">%{row.percent}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600"
                            style={{ width: `${Math.max(4, (Number(row.percent) || 0) / max * 100)}%` }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * The press page — everything a brand needs to judge a creator, on one link.
 *
 * Public by design, so it renders for signed-out visitors too. The backend
 * decides who may see it (published, holding the private link, or a brand
 * this creator applied to); a refusal comes back as a 404 and lands here as
 * the "not published yet" state rather than an error.
 */
function MediaKit() {
    const { t } = useTranslation();
    const { username } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [blocked, setBlocked] = useState(false);
    const [missing, setMissing] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const token = params.get('token');
        api.get(`/api/media-kit/${username}/`, { params: token ? { token } : {} })
            .then(res => {
                setData(res.data);
                document.title = `${username} — ${t('mediaKit.pageTitle')}`;
            })
            .catch(err => {
                if (err.response?.data?.error === 'not_published') setBlocked(true);
                else setMissing(true);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    const copyLink = () => {
        navigator.clipboard.writeText(mediaKitUrl(username)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-screen"><div className="spinner" /></div>;
    }
    if (missing) return <NotFound />;

    if (blocked) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <p className="text-base font-bold text-slate-700">{t('mediaKit.notPublished')}</p>
                <p className="text-sm text-slate-400 mt-1 max-w-sm leading-relaxed">{t('mediaKit.notPublishedDesc')}</p>
                <Link to={`/${username}`} className="btn-primary mt-5 py-2.5 px-5 text-sm no-underline">
                    {t('mediaKit.goToProfile')}
                </Link>
            </div>
        );
    }

    const { user, kit, metrics, rate_card: rateCard, platform_deals: deals,
        reputation, availability, is_owner: isOwner } = data;
    const samples = kit?.sample_content || [];
    // The viewer pages through the samples that are actually media; a link row
    // is a destination, not something to open in a lightbox.
    const playableSamples = samples.filter(isPlayable);

    return (
        <div className="min-h-screen bg-slate-50 pb-20">

            {/* Cover */}
            <div className="relative h-40 sm:h-56 bg-gradient-to-br from-primary-600 to-primary-800 overflow-hidden">
                {(kit?.cover_image || user.background_image) && (
                    <img src={kit?.cover_image || user.background_image} alt=""
                        className="w-full h-full object-cover opacity-80" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-transparent to-black/20" />

                <button onClick={() => navigate(-1)}
                    className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/50 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <button onClick={copyLink}
                    className="absolute top-4 right-4 px-3 h-9 rounded-full bg-black/30 backdrop-blur-md flex items-center gap-1.5 text-white text-xs font-bold hover:bg-black/50 transition">
                    {copied ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                    )}
                    {copied ? t('mediaKit.copied') : t('mediaKit.share')}
                </button>
            </div>

            <div className="max-w-3xl mx-auto px-3 sm:px-4 -mt-14 relative">

                {/* Owner-only banner: an unpublished page still renders for its
                    author, so say plainly that nobody else can reach it. */}
                {isOwner && !data.is_public && (
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 mb-3 flex items-center gap-2.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <p className="text-xs text-amber-800 font-semibold leading-relaxed">
                            {t('mediaKit.ownerDraftBanner')}
                        </p>
                    </div>
                )}

                {/* Identity */}
                <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-4 sm:p-6">
                    <div className="flex items-start gap-4">
                        <img src={user.profile_picture} alt={user.username}
                            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover ring-4 ring-white shadow-lg -mt-10 sm:-mt-12 flex-shrink-0" />
                        <div className="flex-1 min-w-0 pt-1">
                            <div className="flex items-center gap-1.5">
                                <h1 className="text-xl sm:text-2xl font-black text-slate-900 truncate">{user.username}</h1>
                                {user.verified && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01-.622-.636zM6.854 11.354l-2.5-2.5.708-.708L6.854 9.9l3.792-3.793.708.708-4.5 4.5z" />
                                    </svg>
                                )}
                            </div>
                            {kit?.headline && (
                                <p className="text-sm font-semibold text-slate-600 mt-0.5 leading-snug">{kit.headline}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-400">
                                {kit?.location && <span>📍 {kit.location}</span>}
                                {kit?.languages?.length > 0 && <span>🗣 {kit.languages.join(', ')}</span>}
                                {/* Stated, not offered. Getting in touch happens
                                    from the profile now, so this is a detail on
                                    the page rather than a call to action. */}
                                {kit?.contact_email && <span>✉ {kit.contact_email}</span>}
                            </div>
                            {/* Whether they can be booked at all — the first
                                thing a brand needs from this page, so it sits
                                in the identity block rather than further down. */}
                            {hasAvailability(availability) && (
                                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                    <AvailabilityChip availability={availability} />
                                    {availability.note && (
                                        <span className="text-[11px] text-slate-500 italic">{availability.note}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {(kit?.bio || user.profile_text) && (
                        <p className="text-sm text-slate-600 leading-relaxed mt-4 whitespace-pre-line">
                            {kit?.bio || user.profile_text}
                        </p>
                    )}

                    {(user.tags?.length > 0 || user.countrys?.length > 0) && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {user.countrys?.map(country => (
                                <span key={country} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">
                                    {country}
                                </span>
                            ))}
                            {user.tags?.map(tag => (
                                <span key={tag} className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100 text-[11px] font-semibold">
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* One way off this page: the profile, which is where
                        getting in touch, the media kit and the coffee button
                        all live. A second contact route from here only split
                        the conversation across two inboxes. */}
                    <div className="flex flex-wrap gap-2 mt-4">
                        <Link to={`/${user.username}`} className="btn-primary py-2 px-4 text-sm no-underline">
                            {t('mediaKit.viewProfile')}
                        </Link>
                    </div>
                </div>

                {/* Headline numbers */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-3">
                    <Stat value={compact(metrics.followers)} label={t('profile.followers')} />
                    <Stat value={compact(metrics.avg_reach)} label={t('mediaKit.reach')} tone="primary" />
                    <Stat value={`%${metrics.engagement_rate}`} label={t('mediaKit.engagement')} tone="primary" />
                    <Stat value={deals?.count ?? 0} label={t('mediaKit.completedDeals')} tone="emerald" />
                </div>

                {/* The one line that keeps every number above honest. */}
                <p className="text-[11px] text-slate-400 text-center mt-2 leading-relaxed px-4">
                    {t('mediaKit.selfReportedNote')}
                </p>

                <div className="space-y-3 mt-4">

                    {/* Per-platform breakdown */}
                    {metrics.platforms?.length > 0 && (
                        <Section title={t('mediaKit.platformsTitle')}>
                            <div className="space-y-2">
                                {metrics.platforms.map(platform => (
                                    <a key={platform.id} href={platform.link} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-primary-300 hover:bg-primary-50/30 transition no-underline">
                                        <img src={`${apiEndpoint}/media/icons/${(platform.social_media || '').toLowerCase()}.png`}
                                            alt={platform.social_media}
                                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                            onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-slate-800 capitalize">{platform.social_media}</p>
                                            <p className="text-[11px] text-slate-400 truncate">@{platform.username}</p>
                                        </div>
                                        <div className="flex gap-3 sm:gap-4 text-right flex-shrink-0">
                                            <div>
                                                <p className="text-sm font-black text-slate-800 tabular-nums">{compact(platform.followers)}</p>
                                                <p className="text-[9px] text-slate-400 font-semibold uppercase">{t('profile.followers')}</p>
                                            </div>
                                            {platform.avg_reach > 0 && (
                                                <div>
                                                    <p className="text-sm font-black text-slate-800 tabular-nums">{compact(platform.avg_reach)}</p>
                                                    <p className="text-[9px] text-slate-400 font-semibold uppercase">{t('mediaKit.reach')}</p>
                                                </div>
                                            )}
                                            {platform.engagement_rate > 0 && (
                                                <div>
                                                    <p className="text-sm font-black text-primary-700 tabular-nums">%{platform.engagement_rate}</p>
                                                    <p className="text-[9px] text-slate-400 font-semibold uppercase">{t('mediaKit.engagementShort')}</p>
                                                </div>
                                            )}
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Highlights */}
                    {kit?.highlights?.length > 0 && (
                        <Section title={t('mediaKit.highlightsTitle')}>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {kit.highlights.map((row, index) => (
                                    <div key={index} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                                        <p className="text-lg font-black text-slate-800">{row.value}</p>
                                        <p className="text-[11px] text-slate-500 leading-snug">{row.label}</p>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Audience */}
                    {(kit?.audience_countries?.length > 0 || kit?.audience_age?.length > 0 || kit?.audience_gender?.length > 0) && (
                        <Section title={t('mediaKit.audienceTitle')}>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                {kit.audience_countries?.length > 0 && (
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                                            {t('mediaKit.audienceCountries')}
                                        </p>
                                        <SplitBars rows={kit.audience_countries} />
                                    </div>
                                )}
                                {kit.audience_age?.length > 0 && (
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                                            {t('mediaKit.audienceAge')}
                                        </p>
                                        <SplitBars rows={kit.audience_age} />
                                    </div>
                                )}
                                {kit.audience_gender?.length > 0 && (
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                                            {t('mediaKit.audienceGender')}
                                        </p>
                                        <SplitBars rows={kit.audience_gender} />
                                    </div>
                                )}
                            </div>
                        </Section>
                    )}

                    {/* Track record — verified facts, so it sits with the
                        settled-deal section rather than with the numbers the
                        creator typed in themselves. */}
                    {reputation && (reputation.count > 0 || reputation.facts) && (
                        <ReputationPanel reputation={reputation} className="mb-4" />
                    )}

                    {/* Prices */}
                    {rateCard && (rateCard.items?.length > 0 || rateCard.min_budget_usd > 0) && (
                        <Section title={t('rateCard.title')}>
                            <RateCardView card={rateCard} />
                        </Section>
                    )}

                    {/* Collaborations settled through the platform — the only
                        claims on this page we can actually vouch for. */}
                    {deals?.deals?.length > 0 && (
                        <Section title={t('mediaKit.collabsTitle')}
                            badge={<span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-black">
                                {t('mediaKit.verifiedBadge')}
                            </span>}>
                            <div className="space-y-2">
                                {deals.deals.map(deal => (
                                    <div key={deal.id} className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/40 p-2.5">
                                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-white flex-shrink-0">
                                            {deal.cover_image
                                                ? <img src={deal.cover_image} alt="" className="w-full h-full object-cover" />
                                                : <div className="w-full h-full flex items-center justify-center text-slate-300">◎</div>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-slate-800 truncate">{deal.brand}</p>
                                            <p className="text-[11px] text-slate-500 truncate">{deal.campaign}</p>
                                        </div>
                                        <span className="text-[10px] text-slate-400 flex-shrink-0">{formatDate(deal.completed_at)}</span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Collaborations the creator listed themselves */}
                    {kit?.past_collaborations?.length > 0 && (
                        <Section title={deals?.deals?.length > 0
                            ? t('mediaKit.otherCollabsTitle')
                            : t('mediaKit.collabsTitle')}>
                            <div className="flex flex-wrap gap-2">
                                {kit.past_collaborations.map((row, index) => {
                                    const body = (
                                        <>
                                            <p className="text-sm font-bold text-slate-800">{row.brand}</p>
                                            {row.note && <p className="text-[11px] text-slate-500 leading-snug">{row.note}</p>}
                                            {row.year && <p className="text-[10px] text-slate-400 mt-0.5">{row.year}</p>}
                                        </>
                                    );
                                    return row.link ? (
                                        <a key={index} href={row.link} target="_blank" rel="noopener noreferrer"
                                            className="rounded-xl border border-slate-200 px-3 py-2 hover:border-primary-300 transition no-underline">
                                            {body}
                                        </a>
                                    ) : (
                                        <div key={index} className="rounded-xl border border-slate-200 px-3 py-2">{body}</div>
                                    );
                                })}
                            </div>
                        </Section>
                    )}

                    {/* Sample content */}
                    {samples.length > 0 && (
                        <Section title={t('mediaKit.samplesTitle')}>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {samples.map((item, index) => {
                                    const isMedia = isPlayable(item);

                                    if (!isMedia) {
                                        return (
                                            <a key={index} href={item.url} target="_blank" rel="noopener noreferrer"
                                                className="rounded-xl border border-slate-200 p-3 hover:border-primary-300 transition no-underline flex flex-col justify-between">
                                                <p className="text-xs font-bold text-slate-700 break-all line-clamp-3">{item.url}</p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    {item.platform && (
                                                        <span className="text-[10px] font-bold text-slate-500">{platformLabel(item.platform)}</span>
                                                    )}
                                                    {item.views > 0 && (
                                                        <span className="text-[10px] text-slate-400 ml-auto tabular-nums">
                                                            {compact(item.views)} {t('mediaKit.views')}
                                                        </span>
                                                    )}
                                                </div>
                                            </a>
                                        );
                                    }

                                    return (
                                        <MediaTile
                                            key={index}
                                            item={item}
                                            aspect="portrait"
                                            rounded="rounded-xl"
                                            onOpen={() => setSelectedMedia(playableSamples.findIndex(row => row.url === item.url))}
                                        >
                                            {item.views > 0 && (
                                                <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/60 backdrop-blur-md text-white text-[10px] font-bold tabular-nums">
                                                    {compact(item.views)}
                                                </span>
                                            )}
                                        </MediaTile>
                                    );
                                })}
                            </div>
                        </Section>
                    )}
                </div>

                <p className="text-[11px] text-slate-400 text-center mt-6">
                    {kit?.updated_at && `${t('mediaKit.updatedAt')} ${formatDate(kit.updated_at)}`}
                </p>
            </div>

            {/* Media viewer */}
            {selectedMedia !== null && selectedMedia >= 0 && (
                <MediaLightbox
                    items={playableSamples}
                    index={selectedMedia}
                    onIndex={setSelectedMedia}
                    onClose={() => setSelectedMedia(null)}
                />
            )}
        </div>
    );
}

export default MediaKit;
